import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import type {
  BaselineRun,
  BaselineSeriesPoint,
  CoverageSweepRun,
  DriverAttributionSummary,
  EventFlagDay,
  HaloEffectResponse,
  HaloSummary,
  HaloV2StoreCoverage,
  RawEventFlagRow,
} from '@halo/web-types';

async function get<T>(
  path: string,
  signal?: AbortSignal,
  headers?: Record<string, string>,
): Promise<T> {
  const res = await api.get<T>(path, {
    ...(signal ? { signal } : {}),
    ...(headers ? { headers } : {}),
  });
  if (!res.success || res.data === undefined) throw new Error(res.error ?? 'Request failed');
  return res.data;
}

/** Minimal slice of /api/v1/admin/stores used to label store IDs. */
interface AdminStoreItem {
  id: number;
  name: string;
}

export function useAdminStoreNames() {
  return useQuery({
    queryKey: ['halo-v2', 'store-names'],
    queryFn: async ({ signal }) => {
      const stores = await get<AdminStoreItem[]>('/api/v1/admin/stores', signal);
      return new Map(stores.map((s) => [s.id, s.name]));
    },
    staleTime: 5 * 60_000,
  });
}

export function useHaloV2Stores() {
  return useQuery({
    queryKey: ['halo-v2', 'stores'],
    queryFn: ({ signal }) => get<HaloV2StoreCoverage[]>('/api/v1/halo-v2/stores', signal),
    staleTime: 60_000,
  });
}

export interface SeriesParams {
  storeId: number;
  platform: string;
  start: string;
  end: string;
}

export function useBaselineSeries({ storeId, platform, start, end }: SeriesParams) {
  return useQuery({
    queryKey: ['halo-v2', 'series', storeId, platform, start, end],
    queryFn: ({ signal }) =>
      get<BaselineSeriesPoint[]>(
        `/api/v1/halo-v2/baseline-series?storeId=${storeId}&platform=${platform}&start=${start}&end=${end}`,
        signal,
      ),
    enabled: storeId > 0,
  });
}

export function useHaloSummary({ storeId, start, end }: Omit<SeriesParams, 'platform'>) {
  return useQuery({
    queryKey: ['halo-v2', 'summary', storeId, start, end],
    queryFn: ({ signal }) =>
      get<HaloSummary>(
        `/api/v1/halo-v2/halo-summary?storeId=${storeId}&start=${start}&end=${end}`,
        signal,
      ),
    enabled: storeId > 0,
  });
}

export function useEventDays({ storeId, start, end }: Omit<SeriesParams, 'platform'>) {
  return useQuery({
    queryKey: ['halo-v2', 'event-days', storeId, start, end],
    queryFn: ({ signal }) =>
      get<EventFlagDay[]>(
        `/api/v1/halo-v2/event-flags?storeId=${storeId}&start=${start}&end=${end}`,
        signal,
      ),
    enabled: storeId > 0,
  });
}

export function useRawFlags({ storeId, start, end }: Omit<SeriesParams, 'platform'>) {
  return useQuery({
    queryKey: ['halo-v2', 'raw-flags', storeId, start, end],
    queryFn: ({ signal }) =>
      get<RawEventFlagRow[]>(
        `/api/v1/halo-v2/event-flags?storeId=${storeId}&start=${start}&end=${end}&raw=true`,
        signal,
      ),
    enabled: storeId > 0,
  });
}

const ACTIVE_STATES = new Set(['active', 'waiting', 'delayed', 'prioritized']);

export function isRunActive(run: { state: string }): boolean {
  return ACTIVE_STATES.has(run.state);
}

export function useRuns() {
  return useQuery({
    queryKey: ['halo-v2', 'runs'],
    queryFn: ({ signal }) => get<BaselineRun[]>('/api/v1/halo-v2/baseline/runs', signal),
    refetchInterval: (query) => ((query.state.data ?? []).some(isRunActive) ? 3_000 : false),
  });
}

export interface RunRequest {
  mode: 'incremental' | 'backfill';
  storeIds?: number[];
  startDate?: string;
  endDate?: string;
}

export function useTriggerRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: RunRequest) => {
      const res = await api.post<{ jobId: string }>('/api/v1/halo-v2/baseline/run', body);
      if (!res.success || !res.data) throw new Error(res.error ?? 'Run trigger failed');
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['halo-v2', 'runs'] }),
  });
}

export function useCoverageSweepRuns() {
  return useQuery({
    queryKey: ['halo-v2', 'sweep-runs'],
    queryFn: ({ signal }) => get<CoverageSweepRun[]>('/api/v1/halo-v2/coverage-sweep/runs', signal),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((r) => isRunActive(r)) ? 3_000 : false,
  });
}

export function useTriggerCoverageSweep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { cap?: number }) => {
      const res = await api.post<{ jobId: string }>('/api/v1/halo-v2/coverage-sweep/run', body);
      if (!res.success || !res.data) throw new Error(res.error ?? 'Coverage sweep trigger failed');
      return res.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['halo-v2', 'sweep-runs'] }),
  });
}

export function useDriverAttribution({ storeId, platform, start, end }: SeriesParams) {
  return useQuery({
    queryKey: ['halo-v2', 'driver-attribution', storeId, platform, start, end],
    queryFn: ({ signal }) =>
      get<DriverAttributionSummary>(
        `/api/v1/halo-v2/driver-attribution?storeId=${storeId}&platform=${platform}&start=${start}&end=${end}`,
        signal,
      ),
    enabled: storeId > 0,
  });
}

/**
 * Daily halo-effect drivers + per-platform revenue for the drivers comparison chart.
 *
 * The /analytics/haloEffect view derives its store from the X-Store-Id header
 * (not a query param), so we override it with the verify tab's selected store
 * rather than the admin's globally-selected store.
 */
export function useHaloEffectDaily({ storeId, start, end }: Omit<SeriesParams, 'platform'>) {
  return useQuery({
    queryKey: ['halo-v2', 'halo-effect', storeId, start, end],
    queryFn: ({ signal }) =>
      get<HaloEffectResponse>(`/api/v1/analytics/haloEffect?start=${start}&end=${end}`, signal, {
        'X-Store-Id': String(storeId),
      }),
    enabled: storeId > 0,
  });
}

/** Invalidate everything a completed run can change (series, mix, stores coverage). */
export function useInvalidateRunOutputs() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['halo-v2', 'series'] });
    void queryClient.invalidateQueries({ queryKey: ['halo-v2', 'mix'] });
    void queryClient.invalidateQueries({ queryKey: ['halo-v2', 'stores'] });
    void queryClient.invalidateQueries({ queryKey: ['halo-v2', 'summary'] });
    void queryClient.invalidateQueries({ queryKey: ['halo-v2', 'driver-attribution'] });
  }, [queryClient]);
}

export function useUpsertFlags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      storeId: number;
      flags: Array<Partial<Omit<RawEventFlagRow, 'date'>> & { date: string }>;
    }) => {
      const res = await api.post<{ rowsUpserted: number }>('/api/v1/halo-v2/event-flags', body);
      if (!res.success || !res.data) throw new Error(res.error ?? 'Flag upsert failed');
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['halo-v2', 'event-days'] });
      void queryClient.invalidateQueries({ queryKey: ['halo-v2', 'raw-flags'] });
    },
  });
}
